import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { memo } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import ScopeIndicator from "./ScopeIndicator";

interface INodeToolbarProps {
    nodeColor: string;
    title: string;
    subtitle?: string;
    onInfoClick?: () => void;
    /** Optional element rendered after the title (e.g. D365 status icon) */
    titleAdornment?: React.ReactNode;
    /** Entity type icon displayed before the title */
    icon?: SvgIconComponent;
    /** D365 company code — renders a scope line (flag + country name) below subtitle */
    dataAreaId?: string | null;
    /** ISO alpha-2 country code — used when dataAreaId isn't available (e.g. FM data) */
    countryCode?: string | null;
    /** Show "Global" scope indicator */
    global?: boolean;
}

function NodeToolbar({ nodeColor, title, subtitle, onInfoClick, titleAdornment, icon: Icon, dataAreaId, countryCode, global }: INodeToolbarProps) {
    const showScope = global || dataAreaId || countryCode;

    return (
        <Stack
            className="node-drag-handle"
            sx={{
                mx: -0.5,
                mt: -0.5,
                mb: 0.25,
            }}
        >
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: 24,
                }}
            >
                {/* Left: identity (icon + title + adornment) */}
                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {Icon && <Icon sx={{ fontSize: 14, color: nodeColor, display: "flex" }} />}
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            fontWeight: 600,
                            color: nodeColor,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            fontSize: "0.7rem",
                            lineHeight: 1.2,
                        }}
                    >
                        {title}
                    </Typography>
                    {titleAdornment}
                </Stack>
                {/* Right: action (info — hover only) */}
                {onInfoClick && (
                    <IconButton
                        size="small"
                        className="node-info-btn nodrag"
                        onClick={(e) => {
                            e.stopPropagation();
                            onInfoClick();
                        }}
                        sx={{
                            p: 0.25,
                            opacity: 0.3,
                            transition: "opacity 0.15s ease",
                            color: "text.disabled",
                            "&:hover": { color: nodeColor },
                        }}
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </Stack>
            {subtitle && (
                <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                        fontWeight: 600,
                        ml: 2.5,
                        lineHeight: 1.3,
                    }}
                >
                    {subtitle}
                </Typography>
            )}
            {showScope && (
                <Stack sx={{ ml: 2.5 }}>
                    <ScopeIndicator dataAreaId={dataAreaId} countryCode={countryCode} global={global} />
                </Stack>
            )}
        </Stack>
    );
}

export default memo(NodeToolbar);
